/**
 * Breite der Navigation zur Laufzeit (REQ-20 §3.3).
 *
 * Umgesetzt über `data-nav` am `<html>`-Element: `mini` schaltet in
 * `ui/_tokens.scss` `--navw` von 250px auf 66px und `--navlabel` von `inline`
 * auf `none`.
 *
 * Gespeichert wird unter dem bestehenden Schlüssel `SIDEBAR_STATE_KEY` im
 * Format des Altclients (`bool.ToString()`, also `"True"`/`"False"`) — `"True"`
 * heißt ausgeklappt. Ein bestehender Speicherstand passt damit weiterhin, und
 * das Inline-Skript aus `index.html` liest ihn bereits vor dem ersten Rendern.
 */

import { useCallback, useState } from 'react';

import { SIDEBAR_STATE_KEY } from '@/config/appConfig';

export interface NavMiniState {
  /** `true` entspricht der schmalen Navigation. */
  mini: boolean;
  /** Eigenschaft statt Methode: Der Rückgabewert wird destrukturiert. */
  setMini: (value: boolean) => void;
}

/** Ist-Zustand am `<html>`-Element; ohne Attribut gilt die volle Breite. */
function currentMini(): boolean {
  return document.documentElement.dataset.nav === 'mini';
}

export function useNavMini(): NavMiniState {
  const [mini, setState] = useState<boolean>(currentMini);

  const setMini = useCallback((value: boolean): void => {
    if (currentMini() === value) {
      return;
    }

    if (value) {
      document.documentElement.dataset.nav = 'mini';
    } else {
      document.documentElement.removeAttribute('data-nav');
    }

    try {
      window.localStorage.setItem(SIDEBAR_STATE_KEY, value ? 'False' : 'True');
    } catch {
      // Der Altclient protokolliert den Fehler nur auf der Konsole und arbeitet weiter.
    }
    setState(value);
  }, []);

  return { mini, setMini };
}
